import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Box,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { apiClient } from "../../services/apiClient";
import { ArticleCategory } from "../../types/api-types";

interface CreateBundleArticleModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (articleId: string) => void;
  selectedObjectIds: string[];
}

interface BundleArticleForm {
  name: string;
  price: string;
  isPublic: boolean;
  notes: string;
}

const CreateBundleArticleModal: React.FC<CreateBundleArticleModalProps> = ({
  open,
  onClose,
  onSuccess,
  selectedObjectIds,
}) => {
  const [formData, setFormData] = useState<BundleArticleForm>({
    name: "",
    price: "",
    isPublic: false,
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange =
    (field: keyof BundleArticleForm) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError("Artikel-Name ist erforderlich");
      return false;
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      setError("Gültiger Preis ist erforderlich");
      return false;
    }
    if (selectedObjectIds.length === 0) {
      setError("Mindestens ein Mietobjekt muss ausgewählt sein");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setError(null);

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // 1. Artikel erstellen mit isActive: false und category: BUNDLE
      const articleData = {
        name: formData.name.trim(),
        category: ArticleCategory.BUNDLE,
        price: parseFloat(formData.price),
        unit: "Stück",
        isActive: false, // Deaktiviert, nur für Bundles
        isPublic: formData.isPublic,
        basePrice: parseFloat(formData.price),
        currency: "EUR",
        notes: formData.notes.trim() || undefined,
      };

      const createdArticle = await apiClient.createArticle(articleData);

      // 2. Bundle-Artikel zu allen ausgewählten Objekten hinzufügen
      if (selectedObjectIds.length === 1) {
        // Einzelnes Objekt
        await apiClient.addBundleArticleToObject(selectedObjectIds[0], {
          articleId: createdArticle.id,
          quantity: 1,
          bundlePrice: parseFloat(formData.price),
          isPublic: formData.isPublic,
          notes: formData.notes.trim() || undefined,
        });
      } else {
        // Mehrere Objekte (Bulk)
        await apiClient.addBundleArticleToMultipleObjects(selectedObjectIds, {
          articleId: createdArticle.id,
          quantity: 1,
          bundlePrice: parseFloat(formData.price),
          isPublic: formData.isPublic,
          notes: formData.notes.trim() || undefined,
        });
      }

      // Success
      onSuccess(createdArticle.id);
      handleClose();
    } catch (err: any) {
      console.error("Error creating bundle article:", err);
      setError(`Fehler beim Erstellen: ${err.message || "Unbekannter Fehler"}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: "",
        price: "",
        isPublic: false,
        notes: "",
      });
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div">
            Bundle-Artikel erstellen
          </Typography>
          <IconButton onClick={handleClose} disabled={loading} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Für {selectedObjectIds.length} ausgewählte{" "}
          {selectedObjectIds.length === 1 ? "Objekt" : "Objekte"}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box display="flex" flexDirection="column" gap={2}>
          {/* Artikel-Name */}
          <TextField
            label="Artikel-Name"
            value={formData.name}
            onChange={handleInputChange("name")}
            disabled={loading}
            required
            fullWidth
            placeholder="z.B. Premium Bundle"
          />

          {/* Artikel-Preis */}
          <TextField
            label="Preis (€)"
            type="number"
            value={formData.price}
            onChange={handleInputChange("price")}
            disabled={loading}
            required
            fullWidth
            inputProps={{ step: "0.01", min: "0" }}
            helperText="Preis des Artikels und des Bundles"
          />

          {/* Notizen */}
          <TextField
            label="Notizen (optional)"
            value={formData.notes}
            onChange={handleInputChange("notes")}
            disabled={loading}
            multiline
            rows={2}
            fullWidth
            placeholder="Zusätzliche Informationen zum Bundle..."
          />

          {/* isPublic Switch */}
          <FormControlLabel
            control={
              <Switch
                checked={formData.isPublic}
                onChange={handleInputChange("isPublic")}
                disabled={loading}
              />
            }
            label="Öffentlich verfügbar"
            sx={{ alignSelf: "flex-start" }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Abbrechen
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          {loading ? "Erstelle..." : "Erstellen & Hinzufügen"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateBundleArticleModal;
